// English Language Pack
export const en = {
  // Common
  common: {
    search: 'Search...',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    refresh: 'Refresh',
    upload: 'Upload',
    download: 'Download',
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    info: 'Info',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
  },

  // Header
  header: {
    title: 'Onboard Customer Intelligence Platform',
    search: 'Search...',
    theme: {
      light: 'Light Mode',
      dark: 'Dark Mode',
    },
    notifications: 'Notifications',
    aiAssistant: 'AI',
    user: {
      profile: 'User Profile',
      settings: 'Settings',
      logout: 'Logout',
    },
  },

  // Sidebar Menu
  sidebar: {
    brand: 'Onboard AI',
    menu: {
      dashboard: 'Dashboard',
      companyIntel: 'Company Intelligence',
      ocr: 'OCR Processing',
      logic: 'Logic Builder',
      dataManagement: 'Data Management',
      dataManagement_files: 'File Management',
      dataManagement_clean: 'Data Cleaning',
      dataManagement_db: 'SQL DB',
      risk: 'Risk Analysis',
      action: 'Action Board',
      settings: 'Settings',
      settings_integrations: 'API/RSS Settings',
      settings_apiKeys: 'API Keys',
      settings_general: 'General',
    },
  },

  // Status Bar
  statusBar: {
    tasksRunning: 'Running',
    tasksPending: 'Pending',
    tasksCompleted: 'Completed',
    systemStatus: 'System Normal',
    dbConnected: 'DB Connected',
    dbDisconnected: 'Disconnected',
    aiReady: 'AI Ready',
    version: 'V2.0',
  },

  // Dashboard
  dashboard: {
    title: 'Dashboard',
    welcome: 'Welcome to the Onboard Customer Intelligence Platform',
    description: 'AI-powered 360° customer insights, engagement intelligence, and proactive prospect discovery unified in one workspace',
    kpi: {
      totalRisk: 'Total Risk Detection',
      activeModels: 'Active Models',
      newAlerts: 'New Alerts',
      complianceScore: 'Compliance Score',
    },
    charts: {
      riskDistribution: 'Risk Distribution',
      riskTrend: 'Risk Trend Over Time',
    },
    recentAlerts: {
      title: 'Recent Alerts',
      newRegulation: 'New Regulation Update',
      riskDetected: 'Risk Detection Completed',
    },
  },

  // Data Management
  dataManagement: {
    title: 'Data Management',
    description: 'Unified management of data upload, cleaning, and database integration',
    tabs: {
      files: 'Files',
      clean: 'Cleaning',
      db: 'SQL DB',
      merge: 'Merge',
      review: 'Review',
    },
    // File Management
    files: {
      dragDrop: 'Drag and drop files or click to upload',
      supportedFormats: 'Supported formats: Excel (.xlsx, .xls), CSV (.csv)',
      selectedFile: 'Selected file',
      uploading: 'Uploading...',
      uploadSuccess: 'File uploaded successfully',
      uploadError: 'File upload failed',
      fileList: 'Uploaded Files',
      noFiles: 'No files uploaded',
      fileName: 'File Name',
      rows: 'Rows',
      columns: 'Columns',
      size: 'Size',
      uploadTime: 'Upload Time',
      actions: 'Actions',
      preview: 'Preview',
      export: 'Export',
    },
    // Data Cleaning
    clean: {
      title: 'Data Cleaning',
      selectFile: 'Select File',
      selectColumn: 'Select Column',
      mode: 'Cleaning Mode',
      regex: 'Regex',
      replace: 'Replace',
      pattern: 'Pattern',
      replacement: 'Replacement',
      newColumn: 'Create as New Column',
      apply: 'Apply',
      sample: 'Sample Data',
      before: 'Before',
      after: 'After',
    },
    // SQL DB
    db: {
      title: 'SQL DB Connection',
      connected: 'Connected',
      disconnected: 'Disconnected',
      disconnect: 'Disconnect',
      serverInfo: 'Server Info',
      server: 'Server',
      database: 'Database',
      authMode: 'Auth Mode',
      windows: 'Windows Auth',
      sql: 'SQL Server Auth',
      username: 'Username',
      password: 'Password',
      connect: 'Connect',
      connecting: 'Connecting...',
      testConnection: 'Test Connection',
      connectionSuccess: 'Database connected successfully',
      connectionError: 'Database connection failed',
      selectDatabase: 'Select Database',
      selectTable: 'Select Table',
      query: 'Query',
      execute: 'Execute',
      executing: 'Executing...',
      results: 'Results',
      uploadToDb: 'Upload to DB',
      tableName: 'Table Name',
      exportToExcel: 'Export to Excel',
    },
  },

  // Settings
  settings: {
    title: 'Settings',
    description: 'LLM API Key configuration and system settings',
    tabs: {
      apiKeys: 'API Keys',
      general: 'General',
    },
    // API Keys
    apiKeys: {
      title: 'LLM Provider API Keys',
      description: 'Configure API Keys to enable AI features',
      providers: {
        openai: 'OpenAI',
        gemini: 'Google Gemini',
        perplexity: 'Perplexity AI',
        huggingface: 'HuggingFace',
      },
      apiKey: 'API Key',
      model: 'Model',
      enabled: 'Enabled',
      default: 'Set as Default',
      show: 'Show',
      hide: 'Hide',
      save: 'Save',
      saved: 'API Key saved successfully',
      security: {
        title: 'Security Notice',
        storage: 'API Keys are encrypted and stored in local storage',
        production: 'For production, use backend environment variables',
        billing: 'API usage may incur charges from the provider',
        testing: 'This is a test environment. No actual API calls are made',
      },
    },
    // General Settings
    general: {
      title: 'General Settings',
      language: {
        title: 'Language Settings',
        description: 'Select system display language',
        label: 'Language',
        korean: '한국어',
        english: 'English',
        change: 'Change Language',
        changed: 'Language changed successfully',
      },
      theme: {
        title: 'Theme Settings',
        description: 'Select dark or light mode',
        dark: 'Dark Mode',
        light: 'Light Mode',
      },
      notifications: {
        title: 'Notification Settings',
        description: 'Manage system notification preferences',
        enable: 'Enable Notifications',
        sound: 'Sound',
        desktop: 'Desktop Notifications',
      },
    },
  },

  // AI Assistant
  aiAssistant: {
    title: 'AI Assistant',
    placeholder: 'Ask AI...',
    send: 'Send',
    selectLLM: 'Select LLM',
    currentPage: 'Current Page',
    context: 'Context',
  },

  // Tasks
  tasks: {
    title: 'Background Tasks',
    status: {
      pending: 'Pending',
      running: 'Running',
      completed: 'Completed',
      failed: 'Failed',
      cancelled: 'Cancelled',
    },
    actions: {
      cancel: 'Cancel',
      delete: 'Delete',
      retry: 'Retry',
      viewDetails: 'View Details',
    },
    fileReview: 'File Review',
    fileMerge: 'File Merge',
    dataCleaning: 'Data Cleaning',
    noTasks: 'No tasks running',
  },

  // Badges
  badges: {
    new: 'New',
    completed: 'Completed',
    inProgress: 'In Progress',
    connected: 'Connected',
  },

  // Error Messages
  errors: {
    fileUpload: 'File upload failed',
    fileFormat: 'Unsupported file format',
    networkError: 'Network error occurred',
    dbConnection: 'Database connection failed',
    unauthorized: 'Unauthorized',
    notFound: 'Not found',
    serverError: 'Server error occurred',
  },

  // Success Messages
  success: {
    fileUpload: 'File uploaded successfully',
    fileSaved: 'File saved',
    dataClean: 'Data cleaning completed',
    dbConnected: 'Database connected',
    settingsSaved: 'Settings saved',
  },
};

export default en;

